import { GameState, FreeAgent, FreeAgentOffer, Team as TeamInLeague, League, Player } from '../domain/entities';
import { getPlayerValue } from '../domain/constants';
import { addNewsItem } from './GameService';

/**
 * Check if a deadline has passed
 * Returns false if no deadline is set (unlimited time)
 */
const isDeadlinePassed = (gameState: GameState, deadline?: { season: number; week: number }): boolean => {
    if (!deadline) return false; // No deadline = unlimited time
    if (gameState.season > deadline.season) return true;
    if (gameState.season === deadline.season && gameState.currentWeek >= deadline.week) return true;
    return false;
};

export const FreeAgentService = {
    /**
     * Convert expired players to free agents
     */
    convertToFreeAgents: (gameState: GameState, expiredPlayers: { player: Player; team: TeamInLeague; league: League }[]): GameState => {
        const newState = JSON.parse(JSON.stringify(gameState)) as GameState;

        // Ensure freeAgents array exists (for older saves)
        if (!newState.freeAgents) {
            newState.freeAgents = [];
        }

        if (expiredPlayers.length === 0) {
            return newState;
        }

        expiredPlayers.forEach(({ player, team, league }) => {
            const freeAgent: FreeAgent = {
                playerId: player.id,
                playerName: player.name,
                skill: player.skill,
                position: player.position,
                age: player.age,
                potential: player.potential,
                form: player.form || 0,
                fitness: player.fitness,
                wage: player.wage,
                hue: player.hue,
                faceEmoji: player.faceEmoji,
                playerSnapshot: JSON.parse(JSON.stringify(player)), // Deep copy for complete isolation
                originalTeamName: team.name, // Store original team for later removal
                availableSince: { season: newState.season, week: newState.currentWeek },

                // Bidding system: no deadline until first bid arrives
                totalBids: 0,
                userBidStatus: 'no_bid',
                offers: [], // Legacy field
                hasReviewedOffers: false, // Legacy field
            };

            newState.freeAgents.push(freeAgent);
        });

        return newState;
    },

    /**
     * AI teams generate bids for free agents
     * Also increments totalBids count when new bids are made
     */
    processFreeAgentOffers: (gameState: GameState): GameState => {
        const newState = JSON.parse(JSON.stringify(gameState)) as GameState;

        // Ensure freeAgents array exists (for older saves)
        if (!newState.freeAgents) {
            newState.freeAgents = [];
            return newState;
        }

        // For each free agent, generate offers from AI teams
        newState.freeAgents.forEach(freeAgent => {
            // Skip if player already signed or deadline passed
            if (freeAgent.userBidStatus === 'signed' || isDeadlinePassed(newState, freeAgent.bidDeadline)) {
                return;
            }

            const allTeams = Object.values(newState.leagues).flatMap((l: League) => l.teams);

            // Find teams with squad vacancies (< 25 players)
            const teamsWithVacancy = allTeams.filter(t => t.squad.length < 25);

            // Use new offers array or initialize for legacy support
            if (!freeAgent.offers) {
                freeAgent.offers = [];
            }

            teamsWithVacancy.forEach(team => {
                // Skip if player is already on this team (shouldn't happen but safety check)
                if (team.squad.some(p => p.id === freeAgent.playerId)) {
                    return;
                }

                // Skip human player's team - they submit bids through UI
                if (team.name === newState.playerTeamName) {
                    return;
                }

                // Skip if team already bid this week (prevent multiple bids per team)
                if (freeAgent.offers?.some(o => o.teamName === team.name)) {
                    return;
                }

                // AI team evaluation
                const shouldBid = Math.random() < 0.4; // 40% chance to bid
                if (!shouldBid) return;

                // Calculate wage offer based on player quality and team finances
                const baseWage = Math.floor(getPlayerValue(freeAgent as any, '', newState.gameType, newState.config) / 500000);
                const minWage = Math.max(1000, baseWage * 0.8);
                const maxWage = baseWage * 1.5;
                const offeredWage = Math.floor(minWage + Math.random() * (maxWage - minWage));

                // Only bid if team can afford it
                if (team.finances.balance > offeredWage * 4) {
                    const leaguePosition = Object.values(newState.leagues)
                        .flatMap((l: League) => l.teams)
                        .filter(t => t.name === team.name)[0]?.clubHistory?.recordLeaguePosition || 20;

                    const offer: FreeAgentOffer = {
                        teamName: team.name,
                        wage: offeredWage,
                        leaguePosition,
                        leagueName: Object.entries(newState.leagues).find(([_, l]) => l.teams.some(t => t.name === team.name))?.[0],
                    };

                    freeAgent.offers!.push(offer);
                    freeAgent.totalBids++; // Increment bid count
                }
            });
        });

        return newState;
    },

    /**
     * Process free agent decisions when bidding deadline is reached
     * Player chooses the best overall fit based on wages, league prestige, and team quality
     */
    resolveFreeAgentDecisions: (gameState: GameState): GameState => {
        const newState = JSON.parse(JSON.stringify(gameState)) as GameState;

        // Ensure freeAgents array exists (for older saves)
        if (!newState.freeAgents) {
            newState.freeAgents = [];
            return newState;
        }

        newState.freeAgents = newState.freeAgents.filter(freeAgent => {
            // Skip if already signed
            if (freeAgent.userBidStatus === 'signed') {
                return true;
            }

            // Skip if no deadline set yet (waiting for bids) or deadline not yet passed
            if (!freeAgent.bidDeadline || !isDeadlinePassed(newState, freeAgent.bidDeadline)) {
                return true;
            }

            // If user has submitted a bid, DON'T auto-sign - wait for user confirmation
            // User must go to Transfers screen to accept/reject the chosen offer
            if (freeAgent.userBid && freeAgent.userBidStatus === 'submitted') {
                // Mark that deadline has passed so UI knows to show decision
                freeAgent.userBidStatus = 'deadline_reached';
                return true; // Keep in free agents list
            }

            // No bids at all - keep free agent for next week
            const allBids = [...(freeAgent.offers || [])];
            if (freeAgent.userBid) {
                allBids.push({
                    teamName: newState.playerTeamName,
                    wage: freeAgent.userBid.wage,
                    leaguePosition: Object.values(newState.leagues)
                        .flatMap((l: League) => l.teams)
                        .find(t => t.name === newState.playerTeamName)?.clubHistory?.recordLeaguePosition || 20,
                    leagueName: Object.keys(newState.leagues).find(key =>
                        newState.leagues[key].teams.some(t => t.name === newState.playerTeamName)
                    ),
                });
            }

            if (allBids.length === 0) {
                // No bids received - keep free agent
                return true;
            }

            // Only auto-sign if NO user bid (AI only scenario)
            // Player decides based on best overall fit
            const chosenOffer = FreeAgentService.selectBestOffer(freeAgent, allBids);
            if (!chosenOffer) {
                return true; // Keep if unable to choose
            }

            // Find the winning team
            const allTeams = Object.values(newState.leagues).flatMap((l: League) => l.teams);
            const winningTeam = allTeams.find(t => t.name === chosenOffer.teamName);
            if (!winningTeam) {
                return true; // Keep if team not found
            }

            // Get the player object from playerSnapshot (since they're a free agent)
            const playerObj = freeAgent.playerSnapshot ? JSON.parse(JSON.stringify(freeAgent.playerSnapshot)) as Player : null;
            if (!playerObj) {
                return true; // Keep if player not found
            }

            // Reset morale and update wage
            playerObj.morale = 'Happy';
            playerObj.moraleReason = 'Excited about a new challenge';
            playerObj.wage = chosenOffer.wage;

            // Ensure history array exists and add current season entry for new team
            if (!playerObj.history) playerObj.history = [];
            playerObj.history.push({
                season: newState.season,
                teamName: winningTeam.name,
                skill: playerObj.skill,
                gamesPlayed: playerObj.gamesPlayed || 0,
                goalsScored: playerObj.goalsScored || 0,
                goalsConceded: playerObj.goalsConceded || 0,
                assists: playerObj.assists || 0,
                yellowCards: playerObj.yellowCards || 0,
                redCards: playerObj.redCards || 0,
                potmAwards: playerObj.potmAwards || 0,
                averageRating: playerObj.averageRating || 0,
            });

            // Remove original player from their original team (if still there, as a safety check)
            // Handle old saves that might not have originalTeamName by searching for player in all squads
            const originalTeam = freeAgent.originalTeamName ?
                allTeams.find(t => t.name === freeAgent.originalTeamName) :
                allTeams.find(t => t.squad.some(p => p.id === freeAgent.playerId));

            if (originalTeam) {
                originalTeam.squad = originalTeam.squad.filter(p => p.id !== freeAgent.playerId);
            }

            // Add player to winning team
            winningTeam.squad.push(playerObj);
            const signingCost = chosenOffer.wage * 4; // Assume 4 week wages upfront
            winningTeam.finances.balance -= signingCost;

            // Record financial transaction for free agent signing
            winningTeam.finances.history.push({
                season: newState.season,
                week: newState.currentWeek,
                event: `Signed ${freeAgent.playerName} (Free Agent)`,
                amount: -signingCost
            });

            // Record transfer - include original team name for proper history
            newState.transferHistory.push({
                season: newState.season,
                week: newState.currentWeek,
                playerName: freeAgent.playerName,
                playerSkill: freeAgent.skill,
                fromTeam: freeAgent.originalTeamName, // Record actual original team, not 'Free Agent'
                toTeam: winningTeam.name,
                fee: 0, // Free transfer
                playerId: freeAgent.playerId,
                playerAge: freeAgent.age,
                playerPosition: freeAgent.position,
                playerPotential: freeAgent.potential,
            });

            return false; // Remove from free agents
        });

        return newState;
    },

    /**
     * Legacy function for backward compatibility
     * Delegates to resolveFreeAgentDecisions
     */
    resolveFreeAgentOffers: (gameState: GameState): GameState => {
        return FreeAgentService.resolveFreeAgentDecisions(gameState);
    },

    /**
     * Player selects best offer based on multiple factors
     * Considers: wages (40%), league prestige (30%), team quality (20%), player fit (10%)
     * @param freeAgent The free agent
     * @param allBids All bids to consider (both AI and user bids)
     * @returns The chosen offer
     */
    selectBestOffer: (freeAgent: FreeAgent, allBids: FreeAgentOffer[]): FreeAgentOffer | null => {
        if (allBids.length === 0) return null;
        if (allBids.length === 1) return allBids[0];

        // Score each offer based on multiple factors
        const scoredOffers = allBids.map(offer => {
            let score = 0;

            // 1. Wage (40% weight)
            const maxWage = Math.max(...allBids.map(o => o.wage));
            const minWage = Math.min(...allBids.map(o => o.wage));
            const wageRange = maxWage - minWage || 1;
            const normalizedWage = (offer.wage - minWage) / wageRange;
            const wageScore = normalizedWage * 40;
            score += wageScore;

            // 2. League prestige (30% weight)
            // Lower position number = higher prestige (position 1 = best)
            const leaguePos = offer.leaguePosition || 20;
            const prestigeScore = Math.max(0, (1 - (leaguePos - 1) / 19) * 30);
            score += prestigeScore;

            // 3. Team quality (20% weight - based on current league position)
            // Better league position = better team
            const teamQualityScore = Math.max(0, (1 - (leaguePos - 1) / 19) * 20);
            score += teamQualityScore;

            // 4. Player fit (10% weight - slight randomness to make it feel more human)
            // Adds variability so not always deterministic
            const fitScore = Math.random() * 10;
            score += fitScore;

            return { offer, score };
        });

        // Sort by score and return best
        scoredOffers.sort((a, b) => b.score - a.score);
        return scoredOffers[0].offer;
    },

    /**
     * Submit a user bid for a free agent
     * Called when player clicks "Sign" on a free agent
     */
    submitUserBid: (gameState: GameState, freeAgentId: string, wage: number): GameState => {
        const newState = JSON.parse(JSON.stringify(gameState)) as GameState;

        // Ensure freeAgents array exists
        if (!newState.freeAgents) {
            newState.freeAgents = [];
            return newState;
        }

        // Find the free agent
        const freeAgent = newState.freeAgents.find(fa => fa.playerId === freeAgentId);
        if (!freeAgent) {
            return newState;
        }

        // Check if deadline has already passed
        if (isDeadlinePassed(newState, freeAgent.bidDeadline)) {
            return newState;
        }

        // Check if user has already bid on this player this week (1 bid per player per week limit)
        if (freeAgent.userBid &&
            freeAgent.userBid.submittedAt.season === newState.season &&
            freeAgent.userBid.submittedAt.week === newState.currentWeek) {
            return newState;
        }

        // Submit the bid
        freeAgent.userBid = {
            wage,
            submittedAt: { season: newState.season, week: newState.currentWeek },
        };
        freeAgent.userBidStatus = 'submitted';
        freeAgent.totalBids++;

        // Set deadline on first bid (2 weeks from now)
        if (!freeAgent.firstBidReceivedAt) {
            freeAgent.firstBidReceivedAt = { season: newState.season, week: newState.currentWeek };

            // Calculate deadline: 2 weeks from first bid
            let deadlineWeek = newState.currentWeek + 2;
            let deadlineSeason = newState.season;
            const weeksPerSeason = newState.config.WEEKS_PER_SEASON;
            if (deadlineWeek > weeksPerSeason) {
                const weeksOverflow = deadlineWeek - weeksPerSeason;
                deadlineSeason += Math.ceil(weeksOverflow / weeksPerSeason);
                deadlineWeek = weeksOverflow % weeksPerSeason || weeksPerSeason;
            }

            freeAgent.bidDeadline = { season: deadlineSeason, week: deadlineWeek };
        }

        // Add news item
        addNewsItem(
            newState,
            'Bid Submitted',
            `Your bid for ${freeAgent.playerName} at £${wage.toLocaleString()} per week has been submitted. They will decide by Season ${freeAgent.bidDeadline.season} Week ${freeAgent.bidDeadline.week}.`,
            'transfer'
        );

        return newState;
    },

    /**
     * Accept a free agent offer after deadline has been reached
     * User explicitly approves signing the player
     */
    acceptFreeAgentOffer: (gameState: GameState, freeAgentId: string): GameState => {
        const newState = JSON.parse(JSON.stringify(gameState)) as GameState;

        const freeAgentIndex = newState.freeAgents?.findIndex(fa => fa.playerId === freeAgentId);
        if (freeAgentIndex === undefined || freeAgentIndex === -1) {
            return newState;
        }

        const freeAgent = newState.freeAgents![freeAgentIndex];

        // Only allow accepting if deadline has been reached and user has bid
        if (freeAgent.userBidStatus !== 'deadline_reached' || !freeAgent.userBid) {
            return newState;
        }

        // Get winning team (player's team in this case)
        const playerTeam = Object.values(newState.leagues)
            .flatMap((l: League) => l.teams)
            .find(t => t.name === newState.playerTeamName);

        if (!playerTeam) return newState;

        // Get player object
        const playerObj = freeAgent.playerSnapshot ? JSON.parse(JSON.stringify(freeAgent.playerSnapshot)) as Player : null;
        if (!playerObj) return newState;

        // Update player
        playerObj.morale = 'Happy';
        playerObj.moraleReason = 'Excited about a new challenge';
        playerObj.wage = freeAgent.userBid.wage;

        // Ensure history array exists
        if (!playerObj.history) playerObj.history = [];
        playerObj.history.push({
            season: newState.season,
            teamName: playerTeam.name,
            skill: playerObj.skill,
            gamesPlayed: playerObj.gamesPlayed || 0,
            goalsScored: playerObj.goalsScored || 0,
            goalsConceded: playerObj.goalsConceded || 0,
            assists: playerObj.assists || 0,
            yellowCards: playerObj.yellowCards || 0,
            redCards: playerObj.redCards || 0,
            potmAwards: playerObj.potmAwards || 0,
            averageRating: playerObj.averageRating || 0,
        });

        // Remove from original team (safety check)
        const allTeams = Object.values(newState.leagues).flatMap((l: League) => l.teams);
        const originalTeam = allTeams.find(t => t.name === freeAgent.originalTeamName);
        if (originalTeam) {
            originalTeam.squad = originalTeam.squad.filter(p => p.id !== freeAgent.playerId);
        }

        // Add to player's team
        playerTeam.squad.push(playerObj);
        const signingCost = freeAgent.userBid.wage * 4;
        playerTeam.finances.balance -= signingCost;
        playerTeam.finances.history.push({
            season: newState.season,
            week: newState.currentWeek,
            event: `Signed ${freeAgent.playerName} (Free Agent)`,
            amount: -signingCost
        });

        // Record transfer
        newState.transferHistory.push({
            season: newState.season,
            week: newState.currentWeek,
            playerName: freeAgent.playerName,
            playerSkill: freeAgent.skill,
            fromTeam: freeAgent.originalTeamName,
            toTeam: playerTeam.name,
            fee: 0,
            playerId: freeAgent.playerId,
            playerAge: freeAgent.age,
            playerPosition: freeAgent.position,
            playerPotential: freeAgent.potential,
        });

        // Mark as signed and remove from free agents
        freeAgent.userBidStatus = 'signed';
        newState.freeAgents![freeAgentIndex] = freeAgent;

        return newState;
    },

    /**
     * Reject a free agent offer after deadline has been reached
     * User declines signing the player
     */
    rejectFreeAgentOffer: (gameState: GameState, freeAgentId: string): GameState => {
        const newState = JSON.parse(JSON.stringify(gameState)) as GameState;

        const freeAgentIndex = newState.freeAgents?.findIndex(fa => fa.playerId === freeAgentId);
        if (freeAgentIndex === undefined || freeAgentIndex === -1) {
            return newState;
        }

        const freeAgent = newState.freeAgents![freeAgentIndex];

        // Only allow rejecting if deadline has been reached
        if (freeAgent.userBidStatus !== 'deadline_reached') {
            return newState;
        }

        // Reset bid for next week - player goes back into free agent pool
        freeAgent.userBid = undefined;
        freeAgent.userBidStatus = 'no_bid';
        freeAgent.offers = [];
        freeAgent.bidDeadline = undefined;
        freeAgent.firstBidReceivedAt = undefined;

        newState.freeAgents![freeAgentIndex] = freeAgent;

        return newState;
    },
};
